import { z } from 'zod';
import { uuidSchema } from './common';

// ---------------------------------------------------------------------------
// 어드민 대시보드(`dashboard.summary`) — 숫자 카드 넷 · 미처리 상담 · 인기 상품을 한 번에 돌려준다.
// 기간은 반열림 `[from, to)` 이고 클라이언트가 서울 시간 기준 날짜 경계를 ISO 로 보낸다(docs/superpowers/specs/2026-10-26-admin-dashboard-notes.md).
// ---------------------------------------------------------------------------

/** 기간 상한 — 한 해(윤년 포함). 그보다 길면 집계가 무거워지고 화면에서도 쓸 일이 없다. */
export const DASHBOARD_PERIOD_MAX_DAYS = 366;
const DAY_MS = 24 * 60 * 60 * 1000;

export const DASHBOARD_PERIOD_INVALID_MESSAGE = '기간의 끝은 시작보다 뒤여야 합니다.';
export const DASHBOARD_PERIOD_TOO_LONG_MESSAGE = `기간은 최대 ${DASHBOARD_PERIOD_MAX_DAYS}일까지 조회할 수 있습니다.`;

export const dashboardPeriodSchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .superRefine((period, ctx) => {
    const from = Date.parse(period.from);
    const to = Date.parse(period.to);
    if (!(to > from)) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: DASHBOARD_PERIOD_INVALID_MESSAGE });
      return;
    }
    if (to - from > DASHBOARD_PERIOD_MAX_DAYS * DAY_MS) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: DASHBOARD_PERIOD_TOO_LONG_MESSAGE });
    }
  });

export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>;

export const dashboardSummaryInputSchema = z.object({ period: dashboardPeriodSchema });
export type DashboardSummaryInput = z.infer<typeof dashboardSummaryInputSchema>;

const count = z.number().int().nonnegative();

/** 지금 쌓인 것 — 기간과 무관. */
export const dashboardNowStatsSchema = z.object({
  /** 검토 대기(REQUESTED) + 수정 요청(REVISION_REQUESTED). */
  pendingQuotes: z.object({ total: count, requested: count, revisionRequested: count }),
  /** 결제 완료 후 배송 완료 전 — 제작 대기 · 제작 중 · 배송 중. */
  inProgressOrders: z.object({ total: count, waiting: count, inProduction: count, shipped: count }),
});
export type DashboardNowStats = z.infer<typeof dashboardNowStatsSchema>;

/** 기간 안의 것 — 기간 탭이 걸린다. 매출 금액은 두지 않는다. */
export const dashboardPeriodStatsSchema = z.object({
  issuedQuotes: count,
  newUsers: z.object({ total: count, email: count, social: count }),
});
export type DashboardPeriodStats = z.infer<typeof dashboardPeriodStatsSchema>;

/** 미처리 상담 표의 상태 — 검토 대기 · 수정 요청 두 가지만 온다. */
export const DASHBOARD_PENDING_QUOTE_STATUSES = ['REQUESTED', 'REVISION_REQUESTED'] as const;
export type DashboardPendingQuoteStatus = (typeof DASHBOARD_PENDING_QUOTE_STATUSES)[number];

export const dashboardPendingQuoteSchema = z.object({
  quoteRequestId: uuidSchema,
  quoteNumber: z.string(),
  companyName: z.string().nullable(),
  contactName: z.string(),
  status: z.enum(DASHBOARD_PENDING_QUOTE_STATUSES),
  /** 기다리기 시작한 시각 — 검토 대기는 접수 시각, 수정 요청은 요청이 바뀐 시각. */
  waitingSince: z.iso.datetime(),
});
export type DashboardPendingQuote = z.infer<typeof dashboardPendingQuoteSchema>;

export const dashboardPopularProductSchema = z.object({
  productId: uuidSchema,
  productName: z.string(),
  quoteRequests: count,
  /** 요청 중 발행본이 하나라도 있는 비율(0~1). 요청이 없으면 null. */
  issuedRate: z.number().min(0).max(1).nullable(),
  /** 요청 수량 평균. 요청이 없으면 null. */
  averageQuantity: z.number().nonnegative().nullable(),
  paidOrders: count,
});
export type DashboardPopularProduct = z.infer<typeof dashboardPopularProductSchema>;

export const DASHBOARD_LIST_LIMIT = 5;

export const dashboardSummarySchema = z.object({
  generatedAt: z.iso.datetime(),
  period: z.object({ from: z.iso.datetime(), to: z.iso.datetime() }),
  now: dashboardNowStatsSchema,
  periodStats: dashboardPeriodStatsSchema,
  pendingQuotes: z.array(dashboardPendingQuoteSchema).max(DASHBOARD_LIST_LIMIT),
  popularProducts: z.array(dashboardPopularProductSchema).max(DASHBOARD_LIST_LIMIT),
});
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;
